import { Box } from '@mui/material';
import { Plus } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { useMemo } from 'react';

import { InnerActionButtonStyled } from '../../AddEditContactFormStyled';
import { ContactFormPhoneItem, VALID_PHONE_TYPES } from '../..';
import { SelectOption } from 'theSource/components';
import { PhoneNumberField } from './components';

export interface PhoneNumbersFormSectionProps {
  arrayHelpers: FieldArrayRenderProps;
}

export const PhoneNumbersFormSection = ({
  arrayHelpers,
}: PhoneNumbersFormSectionProps) => {
  const phones: ContactFormPhoneItem[] = arrayHelpers.form.values.phones;
  const { getFieldMeta } = useFormikContext();
  const { t } = useTranslation('pages.contacts');

  const handleItemDelete = (index: number) => arrayHelpers.remove(index);
  const handleAddNewItem = () => {
    arrayHelpers.push({
      phone: '',
      type: getNextNewPhoneType(),
    });
  };

  const findInFormByType = (type: string) =>
    phones.find((item: ContactFormPhoneItem) => item.type === type);

  const getNextNewPhoneType = (): string =>
    Object.values(VALID_PHONE_TYPES).find(
      (validType: string) =>
        phones.filter((phone) => phone.type === validType).length === 0
    ) ?? '';

  const defineAvailableTypes = (callerType: string): SelectOption[] => {
    const defineAvailableType = (
      title: string,
      type: string
    ): SelectOption => ({
      label: title,
      value: type,
      disabled: Boolean(
        phones.find((phone) => phone.type === type && phone.type !== callerType)
      ),
    });

    return [
      defineAvailableType(t('mobile'), VALID_PHONE_TYPES.MOBILE),
      defineAvailableType(t('home'), VALID_PHONE_TYPES.HOME),
      defineAvailableType(t('work'), VALID_PHONE_TYPES.WORK),
    ];
  };

  const existingPhoneInputsAreEmptyOrInvalid = useMemo(() => {
    for (let i = 0; i < phones.length; i++) {
      const fieldMeta = getFieldMeta(`phones[${i}].phone`);
      if (!fieldMeta.value || (fieldMeta.touched && Boolean(fieldMeta.error))) {
        return true;
      }
    }
    return false;
  }, [getFieldMeta, phones.length]);

  const isPhoneAdditionButtonHidden =
    findInFormByType(VALID_PHONE_TYPES.MOBILE) &&
    findInFormByType(VALID_PHONE_TYPES.HOME) &&
    findInFormByType(VALID_PHONE_TYPES.WORK);

  const isPhoneAdditionButtonDisabled =
    phones.length >= 3 || existingPhoneInputsAreEmptyOrInvalid;

  return (
    <Box data-testid="contact-edit-phones-form-section">
      {phones.map((_: ContactFormPhoneItem, index: number) => (
        <PhoneNumberField
          key={index}
          index={index}
          onDelete={handleItemDelete}
          getOptions={defineAvailableTypes}
        />
      ))}
      {!isPhoneAdditionButtonHidden && (
        <InnerActionButtonStyled
          data-testid="add-phone-number-button"
          startIcon={<Plus size={16} weight="bold" />}
          label={t('addPhoneNumberButtonLabel')}
          variant="tertiary"
          onClick={handleAddNewItem}
          disabled={isPhoneAdditionButtonDisabled}
        />
      )}
    </Box>
  );
};
